import { BettingState, LEG_TYPES } from "@ppb/betslip-core";
import {
  hasAnyMarketClosedFailure,
  hasAnyMarketSuspendedFailure,
  isMultiBetBuilder,
  isMultiple,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import type { BettingMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";

export type MultipleCombinations = {
  oneLineCombination?: BettingState.Combination;
  multiLinesCombinations: BettingState.Combination[];
};

export const isOrderableCast = (combination: BettingState.Combination, legs: BettingState.LegsMap): boolean => {
  const [legId] = combination.legs;
  const leg = legs[legId];

  return [LEG_TYPES.FORECAST, LEG_TYPES.TRICAST].includes(leg.legType);
};

export const groupCombinationsByMultiLineTypes = (
  combinations: BettingState.CombinationsMap = {},
): MultipleCombinations => {
  const [lowestLineCombination, ...rest] = Object.values(combinations)
    .filter((combination) => isMultiple(combination))
    .sort((combinationA, combinationB) => combinationA.numLines - combinationB.numLines);

  return {
    oneLineCombination: lowestLineCombination,
    multiLinesCombinations: rest || [],
  };
};

export const getMultiBetBuilder = (combinations: BettingState.CombinationsMap): BettingState.Combination | undefined =>
  Object.values(combinations).find((combination) => isMultiBetBuilder(combination));

export const getBetBuilderEventUrn = (metadata: BettingMetadata | undefined): string | undefined =>
  metadata?.type === "RACING" ? metadata.racing.urn : metadata?.eventUrn;

const getUnavailableRunnerCombinationGroups = (
  runnerId: string,
  runners: BettingState.RunnersMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
): number[] => {
  const failures = runnerFailures[runnerId] ?? [];

  if (!hasAnyMarketClosedFailure(failures) && !hasAnyMarketSuspendedFailure(failures)) {
    return [];
  }

  return [
    ...(runners[runnerId]?.combinationGroups ?? []),
    ...failures.flatMap(({ combinationGroups = [] }) => combinationGroups),
  ];
};

export const getUnavailableBetBuilderRunnerIds = (
  combinationGroup: number,
  runners: BettingState.RunnersMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
): string[] =>
  Object.keys(runnerFailures).filter((runnerId) =>
    getUnavailableRunnerCombinationGroups(runnerId, runners, runnerFailures).includes(combinationGroup),
  );

export const getUnavailableBetBuilderLegIds = (
  combinationGroup: number,
  combinationLegIds: string[],
  legs: BettingState.LegsMap,
  runners: BettingState.RunnersMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
): string[] => {
  const unavailableRunnerIds = getUnavailableBetBuilderRunnerIds(combinationGroup, runners, runnerFailures);

  return Object.keys(legs).filter(
    (legId) =>
      !combinationLegIds.includes(legId) &&
      legs[legId].runners.some((runnerId) => unavailableRunnerIds.includes(runnerId)),
  );
};

export const getUnavailableMultiBetBuilderLegIds = (
  activeLegIds: string[],
  legs: BettingState.LegsMap,
  runners: BettingState.RunnersMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
): string[] => {
  const activeCombinationGroups = new Set(
    activeLegIds
      .flatMap((legId) => legs[legId]?.runners ?? [])
      .flatMap((runnerId) => runners[runnerId]?.combinationGroups ?? []),
  );

  if (!activeCombinationGroups.size) {
    return [];
  }

  return Object.keys(legs).filter(
    (legId) =>
      !activeLegIds.includes(legId) &&
      legs[legId].runners.some((runnerId) =>
        getUnavailableRunnerCombinationGroups(runnerId, runners, runnerFailures).some((combinationGroup) =>
          activeCombinationGroups.has(combinationGroup),
        ),
      ),
  );
};

export const getUnavailableBetBuilderCombinationGroups = (
  combinations: BettingState.CombinationsMap,
  runners: BettingState.RunnersMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
): number[] => {
  const activeCombinationGroups = new Set(Object.values(combinations).map(({ combinationGroup }) => combinationGroup));

  return [
    ...new Set(
      Object.keys(runnerFailures).flatMap((runnerId) =>
        getUnavailableRunnerCombinationGroups(runnerId, runners, runnerFailures),
      ),
    ),
  ].filter((combinationGroup) => !activeCombinationGroups.has(combinationGroup));
};
